import cx from "classnames";
import { useMemo, useState } from "react";

import { TERMINAL_META } from "../landingContent";

const CANDLE_COUNT = 46;
const BOOK_ROWS = 6;
const COLLATERAL = 5000;
const TIME_LABELS = ["09:00", "11:00", "13:00", "15:00", "17:00"];

type Candle = { up: boolean; wickBottom: number; wickHeight: number; bodyBottom: number; bodyHeight: number };

/**
 * Deterministic candle series.
 *
 * Seeded so the shape is identical on every render and between server and
 * client — this is decorative product imagery, not a price feed. Swap for real
 * OHLC if this section is ever wired to market data.
 */
function buildChart() {
  let seed = 7;
  const rnd = () => {
    seed = (seed * 1103515245 + 12345) % 2147483648;
    return seed / 2147483648;
  };

  let price = 112800;
  const raw: { o: number; c: number; hi: number; lo: number }[] = [];

  for (let i = 0; i < CANDLE_COUNT; i++) {
    const drift = 130 + Math.sin(i / 6) * 90;
    const open = price;
    price = open + (rnd() - 0.42) * 620 + drift * 0.34;
    raw.push({
      o: open,
      c: price,
      hi: Math.max(open, price) + rnd() * 300,
      lo: Math.min(open, price) - rnd() * 300,
    });
  }

  const high = Math.max(...raw.map((r) => r.hi));
  const low = Math.min(...raw.map((r) => r.lo));
  const span = high - low;
  const pct = (v: number) => ((v - low) / span) * 100;

  const candles: Candle[] = raw.map((r) => {
    const bodyLow = Math.min(r.o, r.c);
    const bodyHigh = Math.max(r.o, r.c);

    return {
      up: r.c >= r.o,
      wickBottom: pct(r.lo),
      /* Floors keep doji candles from collapsing to nothing. */
      wickHeight: Math.max(pct(r.hi) - pct(r.lo), 0.6),
      bodyBottom: pct(bodyLow),
      bodyHeight: Math.max(pct(bodyHigh) - pct(bodyLow), 0.9),
    };
  });

  return { candles, high, low, span, mark: raw[raw.length - 1].c };
}

function buildBook(mark: number, direction: 1 | -1) {
  return Array.from({ length: BOOK_ROWS }, (_, i) => {
    const price = mark + direction * (i + 1) * 5.5;
    const size = 0.42 + ((i * 37 + (direction > 0 ? 11 : 23)) % 100) / 90;

    return {
      price: price.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
      size: size.toFixed(3),
      total: (size * (i + 1) * 1.4).toFixed(2),
      depth: `${Math.round(26 + i * 11)}%`,
    };
  });
}

const usd = (v: number, digits = 2) =>
  `$${v.toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;

const BOOK_ROW = "relative grid grid-cols-3 rounded-3 px-4 py-[2.5px] text-[11.5px] l-mono l-nums";

export function TerminalSection() {
  const [side, setSide] = useState<"long" | "short">("long");
  const [leverage, setLeverage] = useState(10);

  const { candles, high, low, span, mark } = useMemo(buildChart, []);
  const asks = useMemo(() => buildBook(mark, 1).reverse(), [mark]);
  const bids = useMemo(() => buildBook(mark, -1), [mark]);

  const isLong = side === "long";
  const positionSize = COLLATERAL * leverage;
  /* Long liquidates below entry, short above — the 0.92 factor is the
     prototype's maintenance-margin stand-in. */
  const liquidation = isLong ? mark * (1 - 0.92 / leverage) : mark * (1 + 0.92 / leverage);

  const summary = [
    { k: "Position size", v: `$${positionSize.toLocaleString("en-US")}`, color: "var(--ink)" },
    { k: "Entry price", v: usd(mark), color: "var(--ink)" },
    {
      k: "Liquidation",
      v: `$${liquidation.toLocaleString("en-US", { maximumFractionDigits: 0 })}`,
      color: "var(--warning)",
    },
    { k: "Est. slippage", v: "0.0 bps", color: "var(--success)" },
    { k: "Fee (taker)", v: `$${(positionSize * 0.00045).toFixed(2)}`, color: "var(--ink-soft)" },
  ];

  const gridRows = [1, 0.75, 0.5, 0.25, 0].map((f) => (low + span * f).toLocaleString("en-US", {
    maximumFractionDigits: 0,
  }));

  return (
    <section id="terminal" className="relative overflow-hidden border-b" style={{ borderColor: "var(--line)" }}>
      <div
        className="pointer-events-none absolute h-[420px] w-[520px]"
        style={{
          top: "8%",
          left: "-10%",
          background: "radial-gradient(circle, var(--glow-a), transparent 70%)",
          filter: "blur(30px)",
        }}
      />

      <div className="l-container relative py-88">
        <div data-reveal className="mb-44 text-center">
          <p className="l-eyebrow">The Terminal</p>
          <h2 className="l-h2">Intent to fill, without the leak</h2>
          <p className="l-intro" style={{ maxWidth: "58ch", marginInline: "auto" }}>
            Aggregated depth, hardware-timestamped matching and a quoted price that holds through submission.
          </p>
        </div>

        <div
          data-reveal
          className="overflow-hidden rounded-16 border"
          style={{ borderColor: "var(--line-strong)", background: "var(--surface)", boxShadow: "var(--shadow)" }}
        >
          {/* chrome bar */}
          <div
            className="flex flex-wrap items-center gap-12 border-b px-14 py-10"
            style={{ background: "var(--surface-muted)", borderColor: "var(--line)" }}
          >
            <div className="flex flex-none gap-6">
              {["var(--danger)", "var(--warning)", "var(--success)"].map((color) => (
                <span key={color} className="h-10 w-10 rounded-full opacity-65" style={{ background: color }} />
              ))}
            </div>
            <span
              className="l-mono text-[11.5px]"
              style={{ letterSpacing: "0.05em", color: "var(--ink-subtle)" }}
            >
              app.usdx.exchange / trade / BTC-USD
            </span>
            <div className="flex-1" />
            <span className="l-live-chip">
              <span className="l-dot" />
              Connected
            </span>
          </div>

          <div className="l-term-grid gap-1" style={{ background: "var(--line)" }}>
            {/* ---- chart column ---- */}
            <div className="flex flex-col" style={{ background: "var(--surface)" }}>
              <div
                className="flex flex-wrap items-center gap-20 border-b px-18 py-14"
                style={{ borderColor: "var(--line)" }}
              >
                <div className="flex items-center gap-9">
                  <span
                    className="grid h-24 w-24 place-items-center rounded-full text-11 font-extrabold text-white"
                    style={{ background: "#F7931A", fontFamily: "var(--font-display)" }}
                  >
                    B
                  </span>
                  <span
                    className="text-16 font-bold"
                    style={{ fontFamily: "var(--font-display)", letterSpacing: "-0.01em" }}
                  >
                    BTC-USD
                  </span>
                  <span
                    className="rounded-6 px-8 py-2 text-[10.5px] font-bold"
                    style={{ background: "var(--sunken)", color: "var(--ink-subtle)", letterSpacing: "0.06em" }}
                  >
                    PERP
                  </span>
                </div>

                <div className="flex flex-col gap-1">
                  <span
                    className="l-nums text-20 font-bold"
                    style={{ letterSpacing: "-0.02em", color: "var(--success)" }}
                  >
                    {usd(mark)}
                  </span>
                  <span className="l-mono text-11" style={{ color: "var(--ink-subtle)" }}>
                    Mark · +2.14%
                  </span>
                </div>

                {TERMINAL_META.map((m) => (
                  <div key={m.k} className="l-term-meta flex-col gap-2">
                    <span className="l-micro" style={{ letterSpacing: "0.08em", fontSize: 10 }}>
                      {m.k}
                    </span>
                    <span className="l-nums text-13 font-semibold" style={{ color: "var(--ink-soft)" }}>
                      {m.v}
                    </span>
                  </div>
                ))}
              </div>

              <div className="relative flex-1 px-18 pb-8 pt-14" style={{ minHeight: 300 }}>
                <div
                  className="pointer-events-none absolute flex flex-col justify-between"
                  style={{ inset: "14px 18px 30px" }}
                >
                  {gridRows.map((label, i) => (
                    <div key={i} className="flex items-center gap-8">
                      <div className="h-1 flex-1" style={{ background: "var(--line-soft)" }} />
                      <span
                        className="l-mono w-56 text-right text-10"
                        style={{ color: "var(--ink-subtle)" }}
                      >
                        {label}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="relative flex items-end gap-2 pr-64" style={{ height: 270 }}>
                  {candles.map((candle, i) => {
                    const color = candle.up ? "var(--success)" : "var(--danger)";

                    return (
                      <div key={i} className="relative h-full flex-1">
                        <span
                          className="absolute left-1/2 w-[1.5px] -translate-x-1/2 opacity-75"
                          style={{
                            background: color,
                            bottom: `${candle.wickBottom.toFixed(2)}%`,
                            height: `${candle.wickHeight.toFixed(2)}%`,
                          }}
                        />
                        <span
                          className="absolute inset-x-0 rounded-1"
                          style={{
                            background: color,
                            bottom: `${candle.bodyBottom.toFixed(2)}%`,
                            height: `${candle.bodyHeight.toFixed(2)}%`,
                          }}
                        />
                      </div>
                    );
                  })}
                </div>

                <div className="mt-8 flex justify-between pr-64">
                  {TIME_LABELS.map((label) => (
                    <span key={label} className="l-mono text-10" style={{ color: "var(--ink-subtle)" }}>
                      {label}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* ---- order book ---- */}
            <div
              className="flex min-w-0 flex-col gap-10 px-16 py-14"
              style={{ background: "var(--surface)" }}
            >
              <div className="flex items-center justify-between">
                <span className="l-micro" style={{ letterSpacing: "0.09em", fontSize: 11 }}>
                  Order Book
                </span>
                <span className="l-mono text-[10.5px]" style={{ color: "var(--ink-subtle)" }}>
                  0.5
                </span>
              </div>

              <div
                className="grid grid-cols-3 text-[9.5px] font-bold uppercase"
                style={{ letterSpacing: "0.07em", color: "var(--ink-subtle)" }}
              >
                <span>Price</span>
                <span className="text-right">Size</span>
                <span className="text-right">Total</span>
              </div>

              <div className="flex flex-col gap-2">
                {asks.map((row, i) => (
                  <div key={i} className={BOOK_ROW}>
                    <span
                      className="absolute inset-y-0 right-0 rounded-3"
                      style={{ width: row.depth, background: "var(--danger-soft)" }}
                    />
                    <span className="relative" style={{ color: "var(--danger)" }}>
                      {row.price}
                    </span>
                    <span className="relative text-right" style={{ color: "var(--ink-soft)" }}>
                      {row.size}
                    </span>
                    <span className="relative text-right" style={{ color: "var(--ink-subtle)" }}>
                      {row.total}
                    </span>
                  </div>
                ))}
              </div>

              <div
                className="flex items-center justify-between px-4 py-7"
                style={{ borderBlock: "1px solid var(--line)" }}
              >
                <span className="l-nums text-14 font-bold">{usd(mark)}</span>
                <span className="l-mono text-[10.5px]" style={{ color: "var(--success)" }}>
                  spread 0.8 bps
                </span>
              </div>

              <div className="flex flex-col gap-2">
                {bids.map((row, i) => (
                  <div key={i} className={BOOK_ROW}>
                    <span
                      className="absolute inset-y-0 right-0 rounded-3"
                      style={{ width: row.depth, background: "var(--success-soft)" }}
                    />
                    <span className="relative" style={{ color: "var(--success)" }}>
                      {row.price}
                    </span>
                    <span className="relative text-right" style={{ color: "var(--ink-soft)" }}>
                      {row.size}
                    </span>
                    <span className="relative text-right" style={{ color: "var(--ink-subtle)" }}>
                      {row.total}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* ---- order panel ---- */}
            <div
              className="flex min-w-0 flex-col gap-12 px-16 py-14"
              style={{ background: "var(--surface)" }}
            >
              <div
                className="grid grid-cols-2 gap-6 rounded-10 p-3"
                style={{ background: "var(--sunken)" }}
              >
                {(["long", "short"] as const).map((option) => {
                  const active = side === option;

                  return (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setSide(option)}
                      className={cx("cursor-pointer rounded-8 border-0 py-9 text-13 font-bold transition-colors")}
                      style={{
                        background: active
                          ? option === "long"
                            ? "var(--success)"
                            : "var(--danger)"
                          : "transparent",
                        color: active ? "#fff" : "var(--ink-muted)",
                      }}
                    >
                      {option === "long" ? "Long" : "Short"}
                    </button>
                  );
                })}
              </div>

              <div className="flex flex-col gap-6">
                <span className="l-micro" style={{ letterSpacing: "0.08em", fontSize: 10 }}>
                  Collateral
                </span>
                <div
                  className="flex items-center gap-8 rounded-10 border px-12 py-9"
                  style={{ borderColor: "var(--line-strong)", background: "var(--surface-muted)" }}
                >
                  <span className="l-nums flex-1 text-16 font-semibold">5,000.00</span>
                  <span className="l-mono text-12 font-bold" style={{ color: "var(--ink-subtle)" }}>
                    USDC
                  </span>
                </div>
              </div>

              <div className="flex flex-col gap-8">
                <div className="flex items-baseline justify-between">
                  <span className="l-micro" style={{ letterSpacing: "0.08em", fontSize: 10 }}>
                    Leverage
                  </span>
                  <span className="l-nums text-14 font-bold" style={{ color: "var(--brand600)" }}>
                    {leverage}x
                  </span>
                </div>
                <input
                  type="range"
                  min={1}
                  max={50}
                  step={1}
                  value={leverage}
                  aria-label="Leverage"
                  onChange={(e) => setLeverage(Number(e.target.value))}
                  className="w-full cursor-pointer"
                  style={{ accentColor: "var(--brand600)" }}
                />
                <div
                  className="l-mono flex justify-between text-10"
                  style={{ color: "var(--ink-subtle)" }}
                >
                  <span>1x</span>
                  <span>25x</span>
                  <span>50x</span>
                </div>
              </div>

              <div
                className="flex flex-col gap-7 rounded-10 border px-12 py-11"
                style={{ background: "var(--surface-muted)", borderColor: "var(--line-soft)" }}
              >
                {summary.map((row) => (
                  <div key={row.k} className="flex items-center justify-between gap-10">
                    <span className="text-[11.5px]" style={{ color: "var(--ink-muted)" }}>
                      {row.k}
                    </span>
                    <span className="l-nums text-[11.5px] font-semibold" style={{ color: row.color }}>
                      {row.v}
                    </span>
                  </div>
                ))}
              </div>

              <button
                type="button"
                className="usdx-submit w-full cursor-pointer rounded-10 border-0 py-13 text-14 font-bold text-white"
                style={{ background: isLong ? "var(--success)" : "var(--danger)" }}
              >
                {isLong ? "Long" : "Short"} BTC-USD · {leverage}x
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
